/**
 * Junctions that SLIDE along a wall a length edit re-angles.
 *
 * A length edit moves each junction — a tight group of wall ends and floor
 * corners — all-or-nothing: `delta * dir` inside the box, nothing outside.
 * A wall that does not run along `dir` and has one end on each side of the
 * cut is re-angled, and everything ON it rides its affine image: its room
 * walls (`stretchPickOffset`) and its children (`rideReangledWall`). A
 * junction partway along it did not. A partition meeting the wall in a T,
 * and the floor corners there, kept the box's verdict, so the floor's corner
 * moved the whole change while the room wall it should sit on moved a share
 * of it: on Pittsburgh Dev / Scan 2 (`3061EBDD`) a room wall 25% of the way
 * along a 16° wall ended 3.7 cm off its floor, and that one mechanism was 71
 * of the 84 corpus edits that left a room wall off its floor.
 *
 * Such a junction moves to where the walls through it meet the re-angled
 * wall's new line — a partition ending there, carried by its far end, or a
 * wall crossing it that stays put — so the partition keeps its direction and
 * only changes length, and the T stays on the wall. Moving it by the wall's
 * own map instead (`dA + (dB - dA) * t`, along `dir`) also kept it on the
 * wall, but turned every partition square to `dir`: 290 more `tilted-wall`
 * warnings over the corpus for walls with no reason to turn. That map is the
 * fallback, for a junction no single meeting point satisfies.
 *
 * Pure. No React, no Firestore.
 */

import { ENDPOINT_TOL, sameDisp, wallEndpointsXZ } from './wallDisplacement';

const ZERO = { x: 0, z: 0 };
const cross = (u, v) => u.x * v.z - u.z * v.x;

// Where the line `p + s·u` meets the line `q + r·v`, or null when they are
// parallel.
function intersectLines(p, u, q, v) {
  const d = cross(u, v);
  if (Math.abs(d) < 1e-9) return null;
  const s = cross({ x: q.x - p.x, z: q.z - p.z }, v) / d;
  return { x: p.x + u.x * s, z: p.z + u.z * s };
}

const unit = (v) => {
  const len = Math.hypot(v.x, v.z);
  return len > 1e-12 ? { x: v.x / len, z: v.z / len } : null;
};

/**
 * Each wall through the junction other than the host, as the line the
 * junction must stay on, moved by that wall's own move: a wall ENDING there
 * (a partition) carried by its far end, its direction kept; a wall running
 * THROUGH it (a crossing) only when it moves whole or not at all.
 */
function linesThrough(k, j, host, walls, boxDisp) {
  // The ends' junctions are already known (`ia` / `ib`), so no lookup.
  const dispOf = (i) => (i >= 0 && boxDisp[i]) || ZERO;
  const lines = [];
  for (const w of walls) {
    if (w.wall === host.wall) continue;
    const { a, b, dir: v, length } = w.ends;
    if (w.ia === k || w.ib === k) {
      const far = w.ia === k ? b : a;
      const d = dispOf(w.ia === k ? w.ib : w.ia);
      lines.push({ before: far, after: { x: far.x + d.x, z: far.z + d.z }, v });
      continue;
    }
    const through = j.members.some((m) => {
      const along = (m.x - a.x) * v.x + (m.z - a.z) * v.z;
      const off = Math.abs((m.x - a.x) * -v.z + (m.z - a.z) * v.x);
      return along >= 0 && along <= length && off <= w.reach;
    });
    if (!through) continue;
    // A wall that stretches has no single line to keep to; it says nothing.
    const dA = dispOf(w.ia);
    if (!sameDisp(dA, dispOf(w.ib))) continue;
    lines.push({ before: a, after: { x: a.x + dA.x, z: a.z + dA.z }, v });
  }
  return lines;
}

/**
 * The move of the point where `lines` meet the host, the host moved to its
 * new line. Null when the lines disagree (not all parallel, or meeting the
 * new host at different points).
 */
function meetMove(host, lines) {
  const u = host.ends.dir;
  const newA = { x: host.ends.a.x + host.dA.x, z: host.ends.a.z + host.dA.z };
  const newU = unit({
    x: host.ends.b.x + host.dB.x - newA.x,
    z: host.ends.b.z + host.dB.z - newA.z,
  });
  if (!newU) return null;
  let move = null;
  for (const { before, after, v } of lines) {
    if (Math.abs(cross(v, lines[0].v)) > 1e-6) return null;
    const p0 = intersectLines(host.ends.a, u, before, v);
    const p1 = intersectLines(newA, newU, after, v);
    if (!p0 || !p1) return null;
    const m = { x: p1.x - p0.x, z: p1.z - p0.z };
    if (move && !sameDisp(move, m)) return null;
    move = m;
  }
  return move;
}

const shareStorey = (a, b) => {
  if (!a || !b) return true;
  for (const s of a) if (b.has(s)) return true;
  return false;
};

/**
 * @param {Object} structureData - the edit's (storey-scoped) structure
 * @param {Object} graph - `buildJunctionGraph(structureData)`
 * @param {Object} opts
 * @param {Array<{x,z}>} opts.boxDisp - each junction's displacement by the
 *   box, by junction index
 * @param {(wall) => boolean} opts.isReangled - true for a wall that does not
 *   run along the edit's axis (so unequal end moves turn it)
 * @param {Set<number>} [opts.fixedJunctions] - junctions that must keep the
 *   box's verdict (the edited wall's own ends: the edit's length is theirs)
 * @returns {Map<number, {x,z}>} junction index → its displacement, for the
 *   junctions that slide
 */
export function slideJunctionsOnReangledWalls(
  structureData,
  graph,
  { boxDisp, isReangled, fixedJunctions = new Set() }
) {
  // A structure wall with the junctions its ends are in. `reach`: a point
  // meets the wall when it is on its body — within half its thickness of the
  // centreline (a floor corner on its face), plus the tolerance junctions are
  // clustered with.
  const describe = (wall) => {
    if (!wall?.center || !wall.dimensions) return null;
    const ends = wallEndpointsXZ(wall);
    if (!(ends.length > 1e-9)) return null;
    return {
      wall,
      ends,
      ia: graph.indexFor(ends.a),
      ib: graph.indexFor(ends.b),
      reach: (wall.dimensions.z || 0) / 2 + ENDPOINT_TOL,
    };
  };

  // Only the walls the edit re-angles are looked up first: most edits have
  // none, and the plan is rebuilt on every frame of a drag.
  const hosts = [];
  for (const wall of structureData?.walls || []) {
    if (!wall?.center || !wall.dimensions || !isReangled(wall)) continue;
    const w = describe(wall);
    if (!w) continue;
    const { ia, ib } = w;
    if (ia < 0 || ib < 0 || ia === ib) continue;
    const dA = boxDisp[ia];
    const dB = boxDisp[ib];
    if (sameDisp(dA, dB)) continue;
    hosts.push({
      ...w,
      dA,
      dB,
      stories: graph.wallStories?.get(w.wall.id) || null,
    });
  }
  const slides = new Map();
  if (!hosts.length) return slides;
  let walls = null; // every wall, described once a junction needs them

  graph.junctions.forEach((j, k) => {
    if (fixedJunctions.has(k)) return;
    let best = null;
    for (const h of hosts) {
      if (k === h.ia || k === h.ib) continue;
      if (!shareStorey(j.stories, h.stories)) continue;
      const { a, dir: u, length } = h.ends;
      for (const m of j.members) {
        // On the body, not by an end: a point within reach of an end is that
        // end's corner, clustered apart only by scan jitter, and keeps the
        // end's verdict (a 17 cm one on Pittsburgh Dev / Scan 1 slid a hair
        // and refused the edit as slanting the room wall beside it).
        const along = (m.x - a.x) * u.x + (m.z - a.z) * u.z;
        if (along < h.reach || along > length - h.reach) continue;
        const off = Math.abs((m.x - a.x) * -u.z + (m.z - a.z) * u.x);
        if (off > h.reach || (best && off >= best.off)) continue;
        best = { off, t: along / length, h };
      }
    }
    if (!best) return;
    const { t, h } = best;
    // Where its partitions meet the host's new line; failing a single such
    // point (none, or two that disagree — partitions meeting the host from
    // two directions at one corner), the host's own map: a share of its ends'
    // moves, which keeps the junction on the host and lets the partitions
    // turn.
    walls ??= (structureData.walls || []).map(describe).filter(Boolean);
    const lines = linesThrough(k, j, h, walls, boxDisp);
    const move = (lines.length && meetMove(h, lines)) || {
      x: h.dA.x + (h.dB.x - h.dA.x) * t,
      z: h.dA.z + (h.dB.z - h.dA.z) * t,
    };
    slides.set(k, move);
  });
  return slides;
}
